import React, { useState } from "react";
import Button from "../Button";
import { FlaskConical, Trash2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { labTest } from "src/types";
import { useAppDispatch } from "src/redux/hooks";
import { cartListDeleteRequest } from "src/redux/slices/labTestSlice";
import { useLabTestCart } from "src/helpers/common";
import Loader from "src/components/common/Loader";

interface TestDetailsInCartProps {
  testListInCart:
    | labTest.TestItemInCart[]
    | {
        name: string;
        price: number;
        lab_test_id: string;
        code: string;
      }[];
  showAddMoreTest?: boolean;
  disableDeleteTest?: boolean;
  disableAddMoreTest?: boolean;
}

const TestDetailsInCart: React.FC<TestDetailsInCartProps> = ({
  testListInCart,
  showAddMoreTest = true,
  disableDeleteTest = false,
  disableAddMoreTest = false,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const dispatch = useAppDispatch();
  const { fetchLabTestCart } = useLabTestCart();
  const [isLoading, setIsLoading] = useState(false);

  const removeItemFromCart = (code: string) => {
    setIsLoading(true);
    new Promise((resolve, reject) => {
      dispatch(
        cartListDeleteRequest({
          resolve,
          reject,
          payload: { code },
        }),
      );
    })
      .then((res) => {
        fetchLabTestCart();
        if (testListInCart?.length === 1) {
          navigate(`/app/${supportProgramKey}/book-lab-test`);
        }
      })
      .catch((err) => {
        console.error("err", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4">
            <Loader />
            <p className="text-sm text-gray-600">Removing test from cart...</p>
          </div>
        </div>
      )}
      <div className="space-y-4">
        {testListInCart?.map(
          (
            test:
              | labTest.TestItemInCart
              | {
                  name: string;
                  price: number;
                  lab_test_id: string;
                  code: string;
                },
          ) => (
            <div
              key={test?.lab_test_id}
              className="flex items-center justify-between bg-white rounded-md"
            >
              {/* Icon and Test Name */}
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="text-[var(--app-primary)] bg-[var(--icon-bg-primary)] rounded-full p-2">
                  <FlaskConical className="w-4 h-4" />
                </div>
                <span className="text-primary text-sm font-medium truncate overflow-hidden whitespace-nowrap flex-1">
                  {test?.name}
                </span>
              </div>
              {/* Price and Delete */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">₹{test?.price}</span>
                {!disableDeleteTest && (
                  <Trash2
                    className="w-4 h-4 text-red-500 size-8"
                    onClick={() => removeItemFromCart(test?.code)}
                  />
                )}
              </div>
            </div>
          ),
        )}

        {showAddMoreTest && !disableAddMoreTest && (
          <Button
            variant="outlined"
            rounded="full"
            fullWidth
            onClick={() => navigate(`/app/${supportProgramKey}/book-lab-test`)}
          >
            Add more tests
          </Button>
        )}
      </div>
    </>
  );
};

export default TestDetailsInCart;
